import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'

import { apiClient } from '../../../apiClient'
import {
  Category,
  CreateStore,
  CreateStoreResponse,
  EditCategoryResponse,
  EditSection,
  InventoryById,
  InventoryContent,
  InventoryContentData,
  ManageCategory,
  ManageSectionResponse,
  Pagination,
  ProductData,
  Response,
  Sessions,
  SlugResponse,
  StoreCard,
  StoreCardResponse,
  StoreListResponse,
  UpdateInventoryContent,
} from './types'

class StoresApi {
  private api: AxiosInstance

  constructor(config: AxiosRequestConfig) {
    this.api = apiClient.create(config)
  }

  createStore = (payload: CreateStore) =>
    this.api.post<Response<CreateStoreResponse>>(
      `/yoda/internal/v1/stores/create-store`,
      payload,
    )

  updateStore = (payload: CreateStore, store_id: string) =>
    this.api.put<Response<any>>(
      `/yoda/internal/v1/stores/${store_id}/update-store`,
      payload,
    )

  getStoreById = (
    store_id: string,
    country_code?: string,
    language_code?: string,
  ) => {
    if (country_code && language_code) {
      return this.api.get<Response<any>>(
        `/yoda/internal/v1/stores/${store_id}`,
        {
          headers: {
            'x-afb-country-code': country_code,
            'Accept-Language': language_code,
          },
        },
      )
    } else if (country_code) {
      return this.api.get<Response<any>>(
        `/yoda/internal/v1/stores/${store_id}`,
        {
          headers: { 'x-afb-country-code': country_code },
        },
      )
    }
    return this.api.get<Response<any>>(`/yoda/internal/v1/stores/${store_id}`)
  }

  getSlugDuplicate = (slug: string) =>
    this.api.get<Response<SlugResponse>>(
      `/yoda/internal/v1/stores/slug?slug=${slug}`,
    )

  createInventory = (storeId: string, payload: any) =>
    this.api.post<Response<any>>(
      `/yoda/internal/v1/stores/${storeId}/add-product`,
      payload,
    )
  createFaqs = (payload: any) =>
    this.api.post<Response<any>>(`/yoda/internal/v1/faq/create-faq`, payload)

  updateFaqs = (payload: any) =>
    this.api.put<Response<any>>(`/yoda/internal/v1/faq/update-faq`, payload)

  createStoreTime = (store_id: string, payload: Sessions) =>
    this.api.post<Response<any>>(
      `/yoda/internal/v1/stores/${store_id}/store-sessions/bulk`,
      payload,
    )
  getStoreTime = (store_id: string) =>
    this.api.get<Response<Sessions>>(
      `/yoda/internal/v1/stores/${store_id}/store-sessions`,
    )

  updateStoreTime = (store_id: string, payload: Sessions) =>
    this.api.put<Response<any>>(
      `/yoda/internal/v1/stores/${store_id}/store-sessions/bulk`,
      payload,
    )

  getFaqs = (store_id: string) =>
    this.api.get<Response<any>>(
      `/yoda/internal/v1/faq/faq-list?entity_id=${store_id}&faq_type=store`,
    )

  updateStoreStatus = (
    storeId: string,
    status: boolean,
    languageState: string,
    country_code?: string,
  ) => {
    if (country_code) {
      return this.api.patch<Response<any>>(
        `/yoda/internal/v1/stores/${storeId}/update-status`,
        {
          active: status,
          language_code: languageState,
          country_code: country_code,
        },
      )
    }
    return this.api.patch<Response<any>>(
      `/yoda/internal/v1/stores/${storeId}/update-status`,
      { active: status, language_code: languageState },
    )
  }
  updateStorePosition = (payload: any) =>
    this.api.put<Response<any>>(
      `/yoda/internal/v1/stores/update-positions`,
      payload,
    )

  updateStoreMedia = (
    storeId: string,
    action: string,
    introVideoURL: string | null,
    outroVideoURL: string | null,
    clearMedia: boolean,
  ) => {
    console.log("inside the api call storeid", storeId)
    return this.api.patch<Response<any>>(
      `/gw2/yoda/internal/v1/stores/${storeId}/media`,
      {
        action: action,
        intro_video_url: introVideoURL,
        outro_video_url: outroVideoURL,
        clear_media: clearMedia,
      },
    )
  }

  getStoreList = (
    pagination: any,
    page?: 'dropdown' | 'manage_stores' | '',
    country_code?: string,
    active?: any,
    lang?: string,
    sortBy?: 'created_at' | 'active' | 'status' | 'position',
    isArchive?: string | null,
    isActive?: string | null,
    serviceType?: string | null,
    store_id?: string | null,
    title?: string | null,
  ) => {
    const queryParams: { [key: string]: string | number | boolean } = {
      ...pagination,
    }
    if (active !== undefined) {
      queryParams.active = active
    }
    if (isArchive !== undefined && isArchive !== null) {
      queryParams.archived = isArchive === 'true'
    }
    if (isActive !== undefined && isActive !== null) {
      queryParams.active = isActive === 'true'
    }
    queryParams.sort_by = sortBy || 'created_at'
    if (serviceType !== undefined && serviceType !== null) {
      queryParams.service_type = serviceType || ''
    }

    if (page) {
      queryParams.page = page
    }
    const headers: any = {}
    if (lang) {
      headers['Accept-Language'] = lang
    }
    if (country_code) {
      headers['x-afb-country-code'] = country_code
    }
    if (store_id) {
      queryParams.store_id = store_id
    }
    if (title) {
      queryParams.title = title
    }

    return this.api.get<Response<StoreListResponse>>(
      '/yoda/internal/v1/stores/store-list',
      {
        params: queryParams,
        headers,
      },
    )
  }

  cloneStore = (storeId: string, email: string, hubId: string) => {
    return this.api.post(`/gw1/yoda/system/v1/stores/${storeId}/clone`, {
      email,
      hub_id: hubId,
    })
  }

  getProductList = (
    store_id: string,
    country_code?: string,
    sortBy?: 'price' | 'position' | 'created_at',
  ) => {
    const queryParams: any = {}
    if (sortBy) {
      queryParams['sort_by'] = sortBy
    }
    if (country_code) {
      return this.api.get<Response<ProductData>>(
        `/yoda/internal/v1/stores/${store_id}/product-list?limit=500&offset=0`,
        {
          headers: { 'x-afb-country-code': country_code },
          params: queryParams,
        },
      )
    }
    return this.api.get<Response<ProductData>>(
      `/yoda/internal/v1/stores/${store_id}/product-list?limit=500&offset=0`,
      {
        params: queryParams,
      },
    )
  }
  createInventoryItem = (inventory_id: string, payload: any) =>
    this.api.post<Response<any>>(
      `/yoda/internal/v1/stores/${inventory_id}/add-product`,
      payload,
    )
  getInventoryById = (inventoryId: string, country_code: string) =>
    this.api.get<Response<InventoryById>>(
      `/yoda/internal/v1/stores/inventory/${inventoryId}`,
      { headers: { 'x-afb-country-code': country_code } },
    )

  archiveStore = (
    storeId: string,
    payload: {
      user_email?: string
      archive: boolean
    },
  ) => {
    return this.api.post<Response<any>>(
      `/gw1/yoda/internal/v1/stores/${storeId}/archive`,
      payload,
    )
  }
  updateInventoryItem = (inventory_id: string, payload: any) =>
    this.api.put<Response<any>>(
      `/yoda/internal/v1/stores/inventory/${inventory_id}/update-inventory`,
      payload,
    )
  archiveInventoryItem = (inventory_id: string) =>
    this.api.delete<Response<any>>(
      `/yoda/internal/v1/stores/inventory/${inventory_id}/delete-inventory`,
    )
  getStoreCards = (pagination: any) =>
    this.api.get<Response<StoreCardResponse>>(`/yoda/internal/v1/store-cards`, {
      params: pagination,
    })
  updateInventoryPatch = (action: string, payload: any) =>
    this.api.patch<Response<any>>(
      `/gw1/yoda/internal/v1/stores/inventory/bulk`,
      payload,
      {
        params: { action },
      },
    )

  getStoreCardById = (storeCardId: string) =>
    this.api.get<Response<StoreCard>>(
      `/yoda/internal/v1/store-cards/${storeCardId}`,
    )

  createStoreCard = (storeCard: StoreCard) =>
    this.api.post('/yoda/internal/v1/store-cards', storeCard)

  updateStoreCard = (storeCardId: string, storeCard: StoreCard) =>
    this.api.put(`/yoda/internal/v1/store-cards/${storeCardId}`, storeCard)

  deleteStoreCard = (storeCardId: string) =>
    this.api.delete(`/yoda/internal/v1/store-cards/${storeCardId}`)

  updateStatusStoreCard = (storeCardId: string, payload: any) =>
    this.api.patch(`/yoda/internal/v1/store-cards/${storeCardId}`, payload)

  createCategory = (payload: any) =>
    this.api.post('/yoda/internal/v1/categories', payload)

  getCategories = (pagination: any) =>
    this.api.get<Response<ManageCategory>>('/yoda/internal/v1/categories', {
      params: pagination,
    })

  getCategoryById = (id: string) =>
    this.api.get<Response<EditCategoryResponse>>(
      `/yoda/internal/v1/categories/${id}`,
    )
  updateCategory = (categoryId: string, category: Category) =>
    this.api.put(`/yoda/internal/v1/categories/${categoryId}`, category)

  createSection = (payload: any) =>
    this.api.post('/yoda/internal/v1/sections', payload)

  getSections = (pagination: any) =>
    this.api.get<Response<ManageSectionResponse>>(
      `/yoda/internal/v1/sections`,
      {
        params: pagination,
      },
    )

  getSectionById = (id: string) =>
    this.api.get<Response<EditSection>>(`/yoda/internal/v1/sections/${id}`)

  updateSection = (sectionId: string, section: any) =>
    this.api.put(`/yoda/internal/v1/sections/${sectionId}`, section)

  getInventoryContentById = (inventoryId: string, recordType: string) =>
    this.api.get<Response<InventoryContent>>(
      `/gw1/yoda/internal/v1/stores/inventory/${inventoryId}/content?record_type=${recordType}`,
    )

  updateInventoryConent = (
    inventory_id: string,
    payload: UpdateInventoryContent,
  ) =>
    this.api.put<Response<any>>(
      `/gw1/yoda/internal/v1/stores/inventory/${inventory_id}/content`,
      payload,
    )
}

export default StoresApi
